import { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  CirclesThreePlus,
  Scales,
  Trophy,
  Target,
  Lightning,
  ArrowRight
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HeadToHead() {
  const [players, setPlayers] = useState([]);
  const [player1Id, setPlayer1Id] = useState("");
  const [player2Id, setPlayer2Id] = useState("");
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  const player1Options = useMemo(() => players.filter(p => p.id !== player2Id), [players, player2Id]);
  const player2Options = useMemo(() => players.filter(p => p.id !== player1Id), [players, player1Id]);

  const fetchPlayers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/players`);
      setPlayers(response.data);
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  }, []);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const fetchHeadToHead = async () => {
    if (!player1Id || !player2Id || player1Id === player2Id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stats/head-to-head/${player1Id}/${player2Id}`);
      setComparison(response.data);
    } catch (error) {
      console.error("Error fetching head to head:", error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';
  };

  const player1 = players.find(p => p.id === player1Id);
  const player2 = players.find(p => p.id === player2Id);

  const StatComparison = ({ label, value1, value2, icon: Icon, higherIsBetter = true }) => {
    const isP1Better = higherIsBetter ? value1 > value2 : value1 < value2;
    const isP2Better = higherIsBetter ? value2 > value1 : value2 < value1;
    const isTie = value1 === value2;

    return (
      <div className="grid grid-cols-3 gap-4 items-center py-3 border-b border-border last:border-0">
        <div className={`text-right font-heading text-xl font-bold ${isP1Better && !isTie ? 'text-primary' : 'text-foreground'}`}>
          {value1}
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Icon className="w-4 h-4" />
            <span className="text-sm">{label}</span>
          </div>
        </div>
        <div className={`text-left font-heading text-xl font-bold ${isP2Better && !isTie ? 'text-primary' : 'text-foreground'}`}>
          {value2}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
          <CirclesThreePlus className="w-8 h-8 text-primary" weight="fill" />
          Head to Head
        </h1>
        <p className="text-muted-foreground mt-1">
          Compare two players side by side
        </p>
      </div>

      {/* Player Selection */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 w-full">
              <label className="text-sm text-muted-foreground mb-2 block">Player 1</label>
              <Select value={player1Id} onValueChange={setPlayer1Id}>
                <SelectTrigger className="bg-background border-border" data-testid="select-player1">
                  <SelectValue placeholder="Select player" />
                </SelectTrigger>
                <SelectContent>
                  {player1Options.map(player => (
                    <SelectItem key={player.id} value={player.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={player.photo_url} />
                          <AvatarFallback className="text-xs">{getInitials(player.name)}</AvatarFallback>
                        </Avatar>
                        <span>{player.name}</span>
                        <span className="text-muted-foreground text-xs">#{player.ranking}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <Scales className="w-6 h-6 text-muted-foreground" weight="bold" />
              </div>
            </div>

            <div className="flex-1 w-full">
              <label className="text-sm text-muted-foreground mb-2 block">Player 2</label>
              <Select value={player2Id} onValueChange={setPlayer2Id}>
                <SelectTrigger className="bg-background border-border" data-testid="select-player2">
                  <SelectValue placeholder="Select player" />
                </SelectTrigger>
                <SelectContent>
                  {player2Options.map(player => (
                    <SelectItem key={player.id} value={player.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={player.photo_url} />
                          <AvatarFallback className="text-xs">{getInitials(player.name)}</AvatarFallback>
                        </Avatar>
                        <span>{player.name}</span>
                        <span className="text-muted-foreground text-xs">#{player.ranking}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button 
              onClick={fetchHeadToHead}
              disabled={!player1Id || !player2Id || player1Id === player2Id || loading}
              className="bg-primary hover:bg-primary/90"
              data-testid="compare-btn"
            >
              {loading ? "Loading..." : "Compare"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Results */}
      {player1 && player2 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Player Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Player 1 Card */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20 border-2 border-primary">
                    <AvatarImage src={player1.photo_url} />
                    <AvatarFallback className="text-xl">{getInitials(player1.name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-heading text-xl font-bold">{player1.name}</h3>
                    {player1.nickname && (
                      <p className="text-sm text-muted-foreground">"{player1.nickname}"</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        player1.ranking === 1 ? 'rank-1' : 
                        player1.ranking === 2 ? 'rank-2' : 
                        player1.ranking === 3 ? 'rank-3' : 
                        'bg-muted text-muted-foreground'
                      }`}>
                        #{player1.ranking}
                      </span>
                      <span className="text-sm font-medium text-primary">{player1.total_points} pts</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Player 2 Card */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20 border-2 border-secondary">
                    <AvatarImage src={player2.photo_url} />
                    <AvatarFallback className="text-xl">{getInitials(player2.name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-heading text-xl font-bold">{player2.name}</h3>
                    {player2.nickname && (
                      <p className="text-sm text-muted-foreground">"{player2.nickname}"</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        player2.ranking === 1 ? 'rank-1' : 
                        player2.ranking === 2 ? 'rank-2' : 
                        player2.ranking === 3 ? 'rank-3' : 
                        'bg-muted text-muted-foreground'
                      }`}>
                        #{player2.ranking}
                      </span>
                      <span className="text-sm font-medium text-primary">{player2.total_points} pts</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Head to Head Record */}
          {comparison && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-center">Head to Head Record</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center gap-8 py-6">
                  <div className="text-center">
                    <p className="text-5xl font-heading font-bold text-primary">{comparison.player1_wins}</p>
                    <p className="text-sm text-muted-foreground mt-1">Wins</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-heading font-bold text-muted-foreground">-</p>
                  </div>
                  <div className="text-center">
                    <p className="text-5xl font-heading font-bold text-secondary">{comparison.player2_wins}</p>
                    <p className="text-sm text-muted-foreground mt-1">Wins</p>
                  </div>
                </div>
                <p className="text-center text-sm text-muted-foreground">
                  {comparison.total_matches} matches played
                </p>
              </CardContent>
            </Card>
          )}

          {/* Stats Comparison */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-center">Career Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 items-center py-3 border-b border-border">
                <div className="text-right">
                  <Avatar className="h-12 w-12 ml-auto">
                    <AvatarImage src={player1.photo_url} />
                    <AvatarFallback>{getInitials(player1.name)}</AvatarFallback>
                  </Avatar>
                </div>
                <div className="text-center text-sm text-muted-foreground">VS</div>
                <div className="text-left">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={player2.photo_url} />
                    <AvatarFallback>{getInitials(player2.name)}</AvatarFallback>
                  </Avatar>
                </div>
              </div>

              <StatComparison 
                label="Ranking" 
                value1={player1.ranking} 
                value2={player2.ranking}
                icon={Trophy}
                higherIsBetter={false}
              />
              <StatComparison 
                label="Points" 
                value1={player1.total_points} 
                value2={player2.total_points}
                icon={Target}
              />
              <StatComparison 
                label="Matches Won" 
                value1={player1.matches_won} 
                value2={player2.matches_won}
                icon={Trophy}
              />
              <StatComparison 
                label="Win Rate" 
                value1={player1.matches_played > 0 ? Math.round((player1.matches_won / player1.matches_played) * 100) : 0} 
                value2={player2.matches_played > 0 ? Math.round((player2.matches_won / player2.matches_played) * 100) : 0}
                icon={Target}
              />
              <StatComparison 
                label="Centuries" 
                value1={player1.century_breaks} 
                value2={player2.century_breaks}
                icon={Lightning}
              />
              <StatComparison 
                label="High Break" 
                value1={player1.highest_break} 
                value2={player2.highest_break}
                icon={Lightning}
              />
              <StatComparison 
                label="Titles" 
                value1={player1.tournament_wins} 
                value2={player2.tournament_wins}
                icon={Trophy}
              />
            </CardContent>
          </Card>

          {/* Recent Encounters */}
          {comparison && comparison.matches && comparison.matches.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Recent Encounters</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {comparison.matches.slice(0, 5).map((match) => (
                    <div
                      key={match.id}
                      className="flex items-center gap-4 p-3 rounded-lg bg-muted/30"
                    >
                      <div className="flex-1 text-right">
                        <span className={match.winner_id === player1Id ? 'text-primary font-bold' : 'text-muted-foreground'}>
                          {player1.name}
                        </span>
                      </div>
                      <div className="px-4 py-1 rounded bg-background">
                        <span className="font-heading font-bold">
                          {match.player1_id === player1Id ? match.player1_score : match.player2_score}
                        </span>
                        <span className="mx-2 text-muted-foreground">-</span>
                        <span className="font-heading font-bold">
                          {match.player1_id === player1Id ? match.player2_score : match.player1_score}
                        </span>
                      </div>
                      <div className="flex-1">
                        <span className={match.winner_id === player2Id ? 'text-primary font-bold' : 'text-muted-foreground'}>
                          {player2.name}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {match.round.replace('_', ' ')}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      )}

      {/* Empty State */}
      {(!player1Id || !player2Id) && (
        <div className="text-center py-16">
          <Scales className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium text-muted-foreground">Select Two Players</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Choose players from the dropdowns above to compare their stats
          </p>
        </div>
      )}
    </div>
  );
}
